import {
  AiAgentDraftTarget,
  RunAiAgentSseEvent,
} from '../types/runAiAgent.types';

const parseFrame = (raw: string): RunAiAgentSseEvent | undefined => {
  const dataLine = raw.split('\n').find((l) => l.startsWith('data: '));
  if (!dataLine) {
    return undefined;
  }

  try {
    return JSON.parse(dataLine.slice(6)) as RunAiAgentSseEvent;
  } catch {
    // malformed or half-received frame, skip
    return undefined;
  }
};

/** Exit reasons where the agent hands the conversation over instead of replying. */
const handoffExitReasons = new Set(['handoff', 'handoff_break_containment']);

const isHandoff = (exitReason: string | null): boolean =>
  !!exitReason && handoffExitReasons.has(exitReason);

/**
 * Records the exit reason of a status frame that reports a failed run or a
 * handoff. A handoff can end in a `done` status, yet it leaves no reply to draft.
 */
const applyFinalStatus = (
  draft: AiAgentDraftTarget,
  event: RunAiAgentSseEvent,
): void => {
  if (event._type !== 'final_status') {
    return;
  }

  const { exitReason } = event;
  if (event.status === 'failed' || isHandoff(exitReason)) {
    draft.failureReason = exitReason ?? 'unknown';
  }
};

/** Records the tools an assistant message calls, once per tool name. */
const applyToolCalls = (
  draft: AiAgentDraftTarget,
  event: Extract<RunAiAgentSseEvent, { _type: 'message' }>,
): void => {
  for (const { name, displayName, toolType } of event.toolCalls ?? []) {
    if (!name || draft.tools?.some((tool) => tool.name === name)) {
      continue;
    }
    draft.tools = [
      ...(draft.tools ?? []),
      {
        name,
        ...(displayName && { displayName }),
        ...(toolType && { toolType }),
      },
    ];
  }
};

/**
 * Builds a chunk appender for one agent run, so each run buffers and parses
 * its own SSE frames independently of any other concurrent run.
 */
export const createAiAgentMessageAppender = () => {
  let buffer = '';

  return (draft: AiAgentDraftTarget, chunk: string): void => {
    buffer += chunk;
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';

    for (const raw of frames) {
      const event = parseFrame(raw);
      if (!event) continue;

      if (event._type === 'message' && event.role === 'assistant') {
        const textContent = event.content.find((c) => c._type === 'text');
        if (textContent) {
          draft.text = draft.text
            ? draft.text + '\n' + textContent.text
            : textContent.text;
        }
        applyToolCalls(draft, event);
      }

      applyFinalStatus(draft, event);
    }

    // Reads the status of a trailing frame that has no blank line yet.
    const pending = parseFrame(buffer);
    if (pending) {
      applyFinalStatus(draft, pending);
    }
  };
};
